import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Space, Spin } from 'antd';
import type { HistogramData } from '../../../types/domain';
import type { TripHistogramType } from '../../../infrastructure/dataApi/client';
import { useMetaData } from '../../../hooks/data/useMetaData';
import { useHistogramComparisonsData } from '../../../hooks/data/useHistogramComparisonsData';
import { formatAny } from '../../../utils/format';
import { calculateHistogramMaxValue } from '../../../utils/chart';
import { HistogramChart, type HistogramItem } from '../Chart/HistogramChart';
import { SchemaSelector } from './SchemaSelector';

type Props = {
  type: TripHistogramType;
  unit?: string;
  noDataMessage: string;
};

const RANGE_TYPES = {
  traveled_time: 'duration',
  activity_time: 'duration',
  traveled_distance: 'distance',
  dep_time: 'time',
  arr_time: 'time',
  passage_time: 'time',
  staying_population: 'time',
} as const satisfies Record<
  TripHistogramType,
  'time' | 'duration' | 'distance'
>;

const VALUE_TYPES = {
  traveled_time: 'duration',
  activity_time: 'duration',
  traveled_distance: 'distance',
  dep_time: 'time',
  arr_time: 'time',
  passage_time: 'time',
  staying_population: 'count',
} as const satisfies Record<
  TripHistogramType,
  'time' | 'duration' | 'distance' | 'count'
>;

const HistogramComparison = ({
  type,
  unit,
  noDataMessage,
}: Props): ReactNode => {
  const { t } = useTranslation();
  const { allDataMetas } = useMetaData();
  const { isLoading, histogramsMap } = useHistogramComparisonsData(type);

  // 複数のグラフを比較表示する際にy軸のスケールを揃えるため、最大値を計算する
  const maxValue = calculateHistogramMaxValue(
    ...Object.values(histogramsMap).map(
      (histogram: HistogramData): HistogramItem[] => {
        return histogram.items;
      }
    )
  );

  return (
    <>
      <SchemaSelector />
      <Spin spinning={isLoading}>
        <GridContainer>
          {Object.entries(histogramsMap).map(
            ([simDataId, histogram]: [string, HistogramData]): ReactNode => {
              return (
                <Space
                  key={simDataId}
                  direction="vertical"
                  style={{ width: '100%' }}
                >
                  <StatisticContainer>
                    <span>
                      {t('mean')}：
                      {histogram.mean !== null
                        ? formatAny(VALUE_TYPES[type], histogram.mean, unit)
                        : '-'}
                    </span>
                    <span>
                      {t('median')}：
                      {histogram.median !== null
                        ? formatAny(VALUE_TYPES[type], histogram.median, unit)
                        : '-'}
                    </span>
                  </StatisticContainer>
                  <HistogramChart
                    data={histogram.items}
                    rangeType={RANGE_TYPES[type]}
                    maxDomain={maxValue}
                    unit={unit}
                    noDataMessage={noDataMessage}
                    hiddenFilter
                  />
                  <GraphTitle>
                    {allDataMetas.get(simDataId)?.name ?? ''}
                  </GraphTitle>
                </Space>
              );
            }
          )}
        </GridContainer>
      </Spin>
    </>
  );
};

const GridContainer = styled.div`
  margin: 16px 0px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px 24px;
`;

const StatisticContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  font-size: 12px;
`;

const GraphTitle = styled.div`
  font-size: 12px;
  text-align: center;
`;

export { HistogramComparison };
